import logger from '../utils/logger';
import { createClient } from '@supabase/supabase-js';
import { stashImplicitAuthHash } from './authHashRedirect';

// Initialize Supabase client with singleton pattern to avoid multiple instances
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error('❌ Supabase environment variables are not defined!');
  logger.error('Please create a .env file in the frontend directory with:');
  logger.error('VITE_SUPABASE_URL=your-supabase-url');
  logger.error('VITE_SUPABASE_ANON_KEY=your-supabase-anon-key');
}

// Singleton pattern: s'assurer qu'il n'y a qu'une seule instance Supabase
// Cela évite les problèmes de "Multiple GoTrueClient instances" en mode développement
let supabaseInstance = null;

const getSupabaseClient = () => {
  // Si une instance existe déjà dans window, la réutiliser (évite les re-créations en HMR)
  if (typeof window !== 'undefined' && window.__SUPABASE_CLIENT__) {
    return window.__SUPABASE_CLIENT__;
  }
  
  // Si une instance existe déjà en mémoire, la réutiliser
  if (supabaseInstance) {
    return supabaseInstance;
  }
  
  // Créer une nouvelle instance
  // IMPORTANT: For PKCE OAuth to work properly, we MUST use Supabase's default storage
  // DO NOT use custom storage adapters as they can cause getSession() to hang/timeout
  // Supabase's default storage automatically handles:
  // - localStorage for sessions
  // - sessionStorage for PKCE code verifier
  
  logger.debug('✅ Initializing Supabase with default storage (PKCE compatible)');

  // Un lien d'e-mail Supabase (confirmation d'inscription, invitation…) arrive
  // avec la session dans le fragment `#access_token=…`. Le client PKCE le
  // rejette et efface la session locale en émettant SIGNED_OUT ; on retire le
  // fragment de l'URL avant de créer le client (son initialisation est
  // différée, il ne doit jamais le revoir), nos pages de callback le lisent
  // via readAuthHash(). Voir lib/authHashRedirect.js.
  stashImplicitAuthHash();

  supabaseInstance = createClient(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabaseAnonKey || 'placeholder-key',
    {
      auth: {
        persistSession: true,              // ✅ Persiste la session automatiquement
        autoRefreshToken: true,            // ✅ Refresh automatique des tokens
        detectSessionInUrl: true,          // ✅ Active la détection pour OAuth (PKCE traite le code automatiquement)
        flowType: 'pkce'                   // ✅ Utilise PKCE flow (plus sécurisé)
        // ❌ NO custom storage - use Supabase defaults
      },
      global: {
        headers: {
          'x-client-info': 'kaiylo-app'
        }
      }
    }
  );

  // Stocker dans window pour éviter les re-créations en HMR
  if (typeof window !== 'undefined') {
    window.__SUPABASE_CLIENT__ = supabaseInstance;
  }
  
  return supabaseInstance;
};

export const supabase = getSupabaseClient();

